import {
  AppBar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Toolbar,
  Typography
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchCandidateDetails } from "../../../actions/candidates_actions";
import { post } from "../../../actions/api";

const AlertDialog = ({ onClose, title, content }) => (
  <Dialog open onClose={onClose}>
    <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
    <DialogContent>
      <DialogContentText id="alert-dialog-description">{content}</DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} color="primary" autoFocus>
        OK
      </Button>
    </DialogActions>
  </Dialog>
);

export const OtherAssessments = ({ isOnPrintMode }) => {
  const dispatch = useDispatch();
  const { portaldetails, selectedCandidateData } = useSelector((state) => ({
    portaldetails: state.portaldetails,
    selectedCandidateData: state.candidates.selectedCandidateData,
  }));

  const {
    activeIntervention,
    interventions: candidateInterventions = {},
    isInterventionOngoing,
    reAssessmentDue,
    userID,
    assessmentCompleted,
    fullName,
  } = selectedCandidateData;

  const { interventions: organizationInterventions } = portaldetails;
  const [isPending, setPendingStatus] = useState(false);
  const [isSucceeded, setIsSucceeded] = useState(false);
  const [error, setError] = useState(false);

  const doFetchCandidateDetails = (candidateData) => dispatch(fetchCandidateDetails(candidateData));

  const pushIntervention = async (candidateId, interventionId) => {
    setPendingStatus(true);
    try {
      const response = await post(
        `/keyworker/candidate/${candidateId}/intervention`,
        {
          interventionID: interventionId,
        }
      );
      if (response.message) {
        setError(response.message);
        setPendingStatus(false);
        return;
      }
      setIsSucceeded(true);
      setPendingStatus(false);
    } catch (error) {
      console.log(error);
      setPendingStatus(false);
    }
  };

  const totalInterventionIds = new Set(
    (
      organizationInterventions.filter((intervention) => intervention.active) ||
      []
    ).map((i) => i.sort)
  );
  if (activeIntervention) {
    totalInterventionIds.add(activeIntervention.sort);
  }
  if (Object.keys(candidateInterventions).length > 0) {
    totalInterventionIds.add(...Object.keys(candidateInterventions));
  }

  const interventions = Array.from(totalInterventionIds).map(
    (interventionId) => {
      let status = "Not Started";
      let distance_travelled = "";
      if (activeIntervention && interventionId === activeIntervention.sort) {
        status = "Awaiting Start";
      }
      if (candidateInterventions[interventionId]) {
        distance_travelled = "0%";
        if (Number(candidateInterventions[interventionId].initialScore) >= 0) {
          status = "IA Completed";
        }
        if (Number(candidateInterventions[interventionId].finalScore) >= 0) {
          status = "Reassessment Completed";
        }
        if (
          Number(candidateInterventions[interventionId].finalScore) >
          Number(candidateInterventions[interventionId].initialScore)
        ) {
          distance_travelled =
            ((candidateInterventions[interventionId].finalScore -
              candidateInterventions[interventionId].initialScore) /
              10) *
              100 +
            "%";
        }
        if ((candidateInterventions[interventionId].type || 0) === 3) {
          status = "Not Started";
          distance_travelled = "";
          const org = organizationInterventions.find(
            ({ sort }) => sort === interventionId
          );
          if (org) {
            status = "Started";
          }
          if (
            (
              candidateInterventions[interventionId].status || ""
            ).toLowerCase() === "complete"
          ) {
            status = "Completed";
          }
        }
      }

      return {
        ...organizationInterventions.find(
          ({ sort }) => sort === interventionId
        ),
        ...candidateInterventions[interventionId],
        distance_travelled,
        status,
      };
    }
  );

  if (!assessmentCompleted) {
    return (
      <Grid>
        <Grid container item xs justify="center">
          <Typography variant="subtitle2">
            This candidate has not completed the assessment yet.
          </Typography>
        </Grid>
      </Grid>
    );
  }
  return (
    <Grid container direction="column">
      <Grid item size>
        <AppBar
          position="static"
          color="default"
          elevation={0}
          sx={{ mt: 5, mb: 2.5 }}
        >
          <Toolbar variant="dense" sx={{ textAlign: "center", display: "block", pt: 1.75, border: "1px solid #ddd" }}>
            <Typography variant="h6" color="inherit">
              Interventions
            </Typography>
          </Toolbar>
        </AppBar>
      </Grid>
      <Grid item size>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              {!isOnPrintMode && <TableCell>Push</TableCell>}
              <TableCell>Status</TableCell>
              <TableCell>Initial Score</TableCell>
              <TableCell>Final Score</TableCell>
              <TableCell>Distance Travelled</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {interventions
              .filter((i) => i.type === 2)
              .map(
                ({
                  title,
                  sort,
                  status,
                  initialScore,
                  finalScore,
                  distance_travelled,
                  type,
                }) => {
                  const showSendCTA = !isOnPrintMode && type !== 3;
                  return (
                    <TableRow key={sort}>
                      <TableCell>{title}</TableCell>

                      <TableCell>
                        {showSendCTA ? (
                          <Button
                            disabled={
                              isPending ||
                              reAssessmentDue ||
                              isInterventionOngoing
                            }
                            variant="outlined"
                            color="primary"
                            onClick={() => pushIntervention(userID, sort)}
                            endIcon={<ArrowForwardIcon />}
                          >
                            Send
                          </Button>
                        ) : null}
                      </TableCell>

                      <TableCell>{status}</TableCell>
                      <TableCell>{initialScore}</TableCell>
                      <TableCell>{finalScore}</TableCell>
                      <TableCell>{distance_travelled}</TableCell>
                    </TableRow>
                  );
                }
              )}
          </TableBody>
        </Table>
      </Grid>
      {!!error ? (
        <AlertDialog
          title={`Couldn't send push`}
          content={error}
          onClose={() => setError(null)}
        />
      ) : null}
      {!!isSucceeded ? (
        <AlertDialog
          title={`Push sent succesfully`}
          content={`Intervention has been pushed to ${fullName} successfully`}
          onClose={async () => {
            setPendingStatus(true);
            try {
              await doFetchCandidateDetails(selectedCandidateData);
            } catch (error) {
              console.log(error);
            } finally {
              setIsSucceeded(false);
              setPendingStatus(false);
            }
          }}
        />
      ) : null}
    </Grid>
  );
};
